import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getRoles, getUserRoleBindings, createRoleBinding, deleteRoleBinding } from '../../services/iam';
import { toast } from '@/lib/toast';
import { Archive } from 'lucide-react';
import { Button, Checkbox, LoadingRegion, Modal } from '@/components/ds-zta';

interface Props {
  userId: string;
  userName: string;
  onClose: () => void;
}

const ManageUserRolesModal: React.FC<Props> = ({ userId, userName, onClose }) => {
  const queryClient = useQueryClient();

  const { data: rolesData, isLoading: isLoadingRoles } = useQuery(['roles', 'all'], () => getRoles());
  const { data: bindingsData, isLoading: isLoadingBindings } = useQuery(['userRoleBindings', userId], () => getUserRoleBindings(userId));

  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (bindingsData) {
      const initialSelected = new Set(bindingsData.data.map(b => b.role_id));
      setSelectedRoles(initialSelected);
    }
  }, [bindingsData]);

  const handleCheckboxChange = (roleId: string) => {
    setSelectedRoles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roleId)) {
        newSet.delete(roleId);
      } else {
        newSet.add(roleId);
      }
      return newSet;
    });
  };

  const mutation = useMutation(
    async () => {
      const initialBindings = bindingsData?.data || [];
      const initialRoleIds = new Set(initialBindings.map(b => b.role_id));

      const rolesToAdd = [...selectedRoles].filter(id => !initialRoleIds.has(id));
      const bindingsToRemove = initialBindings.filter(b => !selectedRoles.has(b.role_id));

      for (const roleId of rolesToAdd) {
        await createRoleBinding({ subject_type: 'user', subject_id: userId, role_id: roleId, scope_type: 'global' });
      }

      for (const binding of bindingsToRemove) {
        await deleteRoleBinding(binding.id);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userRoleBindings', userId]);
        toast.success('User roles updated successfully');
        onClose();
      },
      onError: (err: any) => {
        toast.error(`Failed to update roles: ${err.message}`);
      },
    }
  );

  const isLoading = isLoadingRoles || isLoadingBindings;

  // Filter out primary roles from the list
  const roles = (rolesData?.data || []).filter(role => !role.is_primary);
  const hasChanges = bindingsData && 
    (bindingsData.data.length !== selectedRoles.size || 
     !bindingsData.data.every(b => selectedRoles.has(b.role_id)));

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <>
          Manage Other Roles <small className="text-[var(--d2d-text-muted)]">({userName})</small>
        </>
      }
      footer={
        <>
          <span className="mr-auto self-center text-sm text-[var(--d2d-text-secondary)]">
            {selectedRoles.size} {selectedRoles.size === 1 ? 'role' : 'roles'} selected
          </span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isLoading} disabled={mutation.isLoading || !hasChanges}>
            {mutation.isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <LoadingRegion lines={6} label="Loading roles" className="py-4" />
      ) : roles.length === 0 ? (
        <div className="text-center py-12">
          <Archive className="mx-auto h-12 w-12 text-[var(--d2d-text-muted)]" />
          <p className="text-[var(--d2d-text-muted)] mt-2">No roles available</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
          {roles.map(role => {
            const isChecked = selectedRoles.has(role.id);
            return (
              <div
                key={role.id}
                className={`p-3 rounded-lg transition-all border ${
                  isChecked
                    ? 'bg-[var(--ds-color-bg-subtle)] border-[var(--d2d-text-primary)]'
                    : 'border-[var(--d2d-border)] hover:bg-[var(--ds-color-bg-hover)]'
                }`}
              >
                <Checkbox
                  id={role.id}
                  checked={isChecked}
                  onChange={() => handleCheckboxChange(role.id)}
                  label={role.name}
                  className="w-full"
                />
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default ManageUserRolesModal;
